import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Input, Button, Row, Col, Table, Typography, Space, Spin, message,
  Popconfirm, Tag, Tabs, Modal, Form, Select, List, Divider, Alert,
  Descriptions, Empty, Statistic, Badge, Tooltip, Switch, InputNumber,
  Checkbox,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EyeOutlined, PlayCircleOutlined,
  SearchOutlined, ReloadOutlined, TagOutlined, GiftOutlined,
  BarChartOutlined, LinkOutlined, StarOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, CloseCircleOutlined, MinusCircleOutlined,
  FileTextOutlined, DownloadOutlined, LoadingOutlined,
  CalendarOutlined, TeamOutlined, HistoryOutlined,
  RobotOutlined, CodeOutlined, BulbOutlined, UserOutlined,
  CopyOutlined, CheckOutlined, GlobalOutlined, SendOutlined, EditOutlined,
  ApartmentOutlined, ThunderboltOutlined, NodeIndexOutlined, RocketOutlined, DollarOutlined,
} from '@ant-design/icons';
import { citationMonitorAPI, billingAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const STABILITY_CONFIG = {
  excellent: { label: '優秀', color: '#52c41a', icon: <StarOutlined /> },
  promising: { label: '有潛力', color: '#1890ff', icon: <CheckCircleOutlined /> },
  unstable: { label: '不穩定', color: '#faad14', icon: <ExclamationCircleOutlined /> },
  danger: { label: '危險', color: '#fa8c16', icon: <CloseCircleOutlined /> },
  never: { label: '從未引用', color: '#f5222d', icon: <MinusCircleOutlined /> },
};

const CATEGORY_LABELS = {
  brand: '品牌詞',
  product: '產品詞',
  industry: '產業問題',
  competitor: '競爭對手',
};

const PLATFORM_LABELS = {
  claude: 'Claude',
  perplexity: 'Perplexity',
  openai: 'ChatGPT',
  gemini: 'Gemini',
};

const WEEKDAY_LABELS = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];

const sentimentColor = (s) => {
  if (!s) return '#999';
  if (s >= 4) return '#52c41a';
  if (s >= 3) return '#1890ff';
  if (s >= 2) return '#faad14';
  return '#f5222d';
};

const sentimentLabel = (s) => {
  if (!s) return '未知';
  if (s >= 4.5) return '非常正面';
  if (s >= 3.5) return '正面';
  if (s >= 2.5) return '中性';
  if (s >= 1.5) return '負面';
  return '非常負面';
};

// Simple copy button component
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button size="small" icon={copied ? <CheckOutlined /> : <CopyOutlined />} onClick={handleCopy}>
      {copied ? '已複製' : '複製'}
    </Button>
  );
}

export default function CitationMonitorPage() {
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subGateOpen, setSubGateOpen] = useState(false);
  const [checkingSub, setCheckingSub] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getStatus();
        const active = res.data?.ai_seo_subscription_active === true;
        setSubscriptionStatus(active);
      } catch { setSubscriptionStatus(false); }
      setCheckingSub(false);
    })();
  }, []);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [queries, setQueries] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [running, setRunning] = useState(false);
  const [brandModal, setBrandModal] = useState(false);
  const [keywordModal, setKeywordModal] = useState(false);
  const [whitelistModal, setWhitelistModal] = useState(false);
  const [queryDetail, setQueryDetail] = useState(null);
  const [brandForm] = Form.useForm();
  const [keywordForm] = Form.useForm();
  const [whitelistForm] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [llmsForm] = Form.useForm();
  const [schemaForm] = Form.useForm();
  const [aioForm] = Form.useForm();
  const [personaForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [runPlatforms, setRunPlatforms] = useState(['claude']);
  const [reportModal, setReportModal] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Feature 1: Schedule
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Feature 3: Stability history
  const [stabilityHistory, setStabilityHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Feature 4: llms.txt
  const [llmsTxtResult, setLlmsTxtResult] = useState('');
  const [generatingLlms, setGeneratingLlms] = useState(false);

  // Feature 5: Schema
  const [schemaResult, setSchemaResult] = useState('');
  const [generatingSchema, setGeneratingSchema] = useState(false);

  // Feature 6: AIO
  const [aioIntents, setAioIntents] = useState([]);
  const [generatingAio, setGeneratingAio] = useState(false);

  // Feature 7: Persona
  const [personaResult, setPersonaResult] = useState(null);
  const [generatingPersona, setGeneratingPersona] = useState(false);

  // Feature 8: GEO Lite
  const [geoLiteForm] = Form.useForm();
  const [geoLiteMap, setGeoLiteMap] = useState(null);
  const [generatingGeoLite, setGeneratingGeoLite] = useState(false);
  const [geoLiteMapId, setGeoLiteMapId] = useState(null);
  const [geoLiteCreatedAt, setGeoLiteCreatedAt] = useState(null);

  // Feature 9: GEO Content
  const [geoContentForm] = Form.useForm();
  const [geoContents, setGeoContents] = useState([]);
  const [generatingGeoContent, setGeneratingGeoContent] = useState(false);
  const [expandedContent, setExpandedContent] = useState(null);

  // Feature 10: FAQ Knowledge Base
  const [faqForm] = Form.useForm();
  const [faqGenForm] = Form.useForm();
  const [faqs, setFaqs] = useState([]);
  const [faqJsonLd, setFaqJsonLd] = useState('');
  const [generatingFaqs, setGeneratingFaqs] = useState(false);
  const [savingFaq, setSavingFaq] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqModal, setFaqModal] = useState(false);

  const fetchBrands = useCallback(async () => {
    setLoadingBrands(true);
    try {
      const res = await citationMonitorAPI.listBrands();
      setBrands(res.data || []);
    } catch { /* ignore */ }
    setLoadingBrands(false);
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const loadDashboard = useCallback(async (brandId) => {
    if (!brandId) return;
    setLoadingDashboard(true);
    try {
      const res = await citationMonitorAPI.getDashboard(brandId);
      setDashboard(res.data);
    } catch { setDashboard(null); }
    setLoadingDashboard(false);
  }, []);

  const loadQueries = useCallback(async (brandId) => {
    if (!brandId) return;
    try {
      const res = await citationMonitorAPI.listQueries(brandId);
      setQueries(res.data || []);
    } catch { setQueries([]); }
  }, []);

  const loadReports = useCallback(async (brandId) => {
    if (!brandId) return;
    setLoadingReports(true);
    try {
      const res = await citationMonitorAPI.listReports(brandId);
      setReports(res.data || []);
    } catch { setReports([]); }
    setLoadingReports(false);
  }, []);

  const loadStabilityHistory = useCallback(async (brandId) => {
    if (!brandId) return;
    setLoadingHistory(true);
    try {
      const res = await citationMonitorAPI.getStabilityHistory(brandId);
      setStabilityHistory(res.data || []);
    } catch { setStabilityHistory([]); }
    setLoadingHistory(false);
  }, []);

  const selectBrand = async (brand) => {
    setSelectedBrand(brand);
    setActiveTab('dashboard');
    setAioIntents([]);
    setPersonaResult(null);
    setGeoLiteMap(null);
    setGeoLiteMapId(null);
    setGeoLiteCreatedAt(null);
    setGeoContents([]);
    setExpandedContent(null);
    setFaqs([]);
    setFaqJsonLd('');
    const detail = await citationMonitorAPI.getBrand(brand.id);
    const brandData = detail.data;
    setSelectedBrand(brandData);
    loadDashboard(brand.id);
    loadQueries(brand.id);
    loadReports(brand.id);
    // Pre-fill schedule form
    scheduleForm.setFieldsValue({
      auto_monitor_enabled: brandData.auto_monitor_enabled || false,
      auto_monitor_day: brandData.auto_monitor_day ?? 1,
      auto_monitor_platforms: brandData.auto_monitor_platforms || ['claude'],
      auto_report_enabled: brandData.auto_report_enabled || false,
      industry: brandData.industry || '',
      competitors: (brandData.competitors || []).join('\n'),
    });
    // Load persisted AIO and Persona results
    try {
      const aioRes = await citationMonitorAPI.getLatestAio(brand.id);
      if (aioRes.data?.intents?.length > 0) setAioIntents(aioRes.data.intents);
    } catch { /* no saved AIO yet */ }
    try {
      const personaRes = await citationMonitorAPI.getLatestPersona(brand.id);
      if (personaRes.data?.persona) setPersonaResult(personaRes.data.persona);
    } catch { /* no saved persona yet */ }
    // Load persisted GEO Lite and GEO Content
    try {
      const geoRes = await citationMonitorAPI.getGeoLite(brand.id);
      if (geoRes.data?.map_data) {
        setGeoLiteMap(geoRes.data.map_data);
        setGeoLiteMapId(geoRes.data.map_id);
        setGeoLiteCreatedAt(geoRes.data.created_at);
      }
    } catch { /* no saved geo lite yet */ }
    loadGeoContent(brand.id);
    loadFaqs(brand.id);
  };

  const handleGenerateReport = async (values) => {
    if (!selectedBrand) return;
    setGeneratingReport(true);
    try {
      await citationMonitorAPI.generateReport(selectedBrand.id, values);
      message.success('建議方案報告已生成！');
      setReportModal(false);
      reportForm.resetFields();
      loadReports(selectedBrand.id);
      setActiveTab('reports');
    } catch (e) {
      message.error('報告生成失敗：' + (e.message || ''));
    }
    setGeneratingReport(false);
  };

  const handleCreateBrand = async (values) => {
    try {
      await citationMonitorAPI.createBrand(values);
      message.success('品牌已建立');
      setBrandModal(false);
      brandForm.resetFields();
      fetchBrands();
    } catch (e) { message.error('建立失敗：' + (e.message || '')); }
  };

  const handleDeleteBrand = async (id) => {
    try {
      await citationMonitorAPI.deleteBrand(id);
      message.success('已刪除');
      if (selectedBrand?.id === id) {
        setSelectedBrand(null);
        setDashboard(null);
      }
      fetchBrands();
    } catch (e) { message.error('刪除失敗'); }
  };

  const handleAddKeyword = async (values) => {
    if (!selectedBrand) return;
    try {
      await citationMonitorAPI.addKeyword(selectedBrand.id, values);
      message.success('關鍵字已新增');
      setKeywordModal(false);
      keywordForm.resetFields();
      const detail = await citationMonitorAPI.getBrand(selectedBrand.id);
      setSelectedBrand(detail.data);
    } catch (e) { message.error('新增失敗：' + (e.message || '')); }
  };

  const handleDeleteKeyword = async (kwId) => {
    try {
      await citationMonitorAPI.deleteKeyword(kwId);
      message.success('已刪除');
      const detail = await citationMonitorAPI.getBrand(selectedBrand.id);
      setSelectedBrand(detail.data);
    } catch { message.error('刪除失敗'); }
  };

  const handleAddWhitelist = async (values) => {
    if (!selectedBrand) return;
    try {
      await citationMonitorAPI.addWhitelist(selectedBrand.id, values);
      message.success('白名單已新增');
      setWhitelistModal(false);
      whitelistForm.resetFields();
      const detail = await citationMonitorAPI.getBrand(selectedBrand.id);
      setSelectedBrand(detail.data);
    } catch (e) { message.error('新增失敗'); }
  };

  const handleDeleteWhitelist = async (wlId) => {
    try {
      await citationMonitorAPI.deleteWhitelist(wlId);
      message.success('已刪除');
      const detail = await citationMonitorAPI.getBrand(selectedBrand.id);
      setSelectedBrand(detail.data);
    } catch { message.error('刪除失敗'); }
  };

  const handleRunMonitor = async () => {
    if (!subscriptionStatus) { setSubGateOpen(true); return; }
    if (!selectedBrand) return;
    setRunning(true);
    try {
      const result = await citationMonitorAPI.runMonitor(selectedBrand.id, { platforms: runPlatforms });
      const data = result.data;
      message.success(`監測完成！建立 ${data.queries_created} 筆查詢`);
      loadDashboard(selectedBrand.id);
      loadQueries(selectedBrand.id);
    } catch (e) {
      message.error('監測失敗：' + (e.message || ''));
    }
    setRunning(false);
  };

  const handleViewQuery = async (queryId) => {
    try {
      const res = await citationMonitorAPI.getQuery(queryId);
      setQueryDetail(res.data);
    } catch { message.error('載入失敗'); }
  };

  // Feature 1: Save schedule settings
  const handleSaveSchedule = async (values) => {
    if (!selectedBrand) return;
    setSavingSchedule(true);
    try {
      const competitorText = values.competitors || '';
      const competitors = competitorText.split('\n').map(s => s.trim()).filter(Boolean);
      const payload = {
        auto_monitor_enabled: values.auto_monitor_enabled,
        auto_monitor_day: values.auto_monitor_day,
        auto_monitor_platforms: values.auto_monitor_platforms,
        auto_report_enabled: values.auto_report_enabled,
        industry: values.industry,
        competitors,
      };
      const res = await citationMonitorAPI.updateSchedule(selectedBrand.id, payload);
      setSelectedBrand(res.data);
      message.success('自動排程設定已儲存');
    } catch (e) {
      message.error('儲存失敗：' + (e.message || ''));
    }
    setSavingSchedule(false);
  };

  // Feature 4: Generate llms.txt
  const handleGenerateLlms = async (values) => {
    if (!selectedBrand) return;
    setGeneratingLlms(true);
    setLlmsTxtResult('');
    try {
      const res = await citationMonitorAPI.generateLlmsTxt(selectedBrand.id, values);
      setLlmsTxtResult(res.data.content || '');
      message.success('llms.txt 已生成！');
    } catch (e) {
      message.error('生成失敗：' + (e.message || ''));
    }
    setGeneratingLlms(false);
  };

  // Feature 5: Generate Schema
  const handleGenerateSchema = async (values) => {
    if (!selectedBrand) return;
    setGeneratingSchema(true);
    setSchemaResult('');
    try {
      const payload = {
        schema_type: values.schema_type,
        brand_name: values.brand_name || selectedBrand.brand_name,
        description: values.description || selectedBrand.description || '',
        url: values.url,
        extra: {},
      };
      const res = await citationMonitorAPI.generateSchema(selectedBrand.id, payload);
      setSchemaResult(res.data.json_ld || '');
      message.success('Schema 標記已生成！');
    } catch (e) {
      message.error('生成失敗：' + (e.message || ''));
    }
    setGeneratingSchema(false);
  };

  // Feature 6: AIO Analysis
  const handleAioAnalysis = async (values) => {
    if (!selectedBrand) return;
    setGeneratingAio(true);
    setAioIntents([]);
    try {
      const res = await citationMonitorAPI.aioAnalysis(selectedBrand.id, {
        market: values.market || 'TW',
        count: values.count || 30,
      });
      setAioIntents(res.data.intents || []);
      message.success(`AIO 分析完成，共 ${res.data.total} 個意圖群組`);
    } catch (e) {
      message.error('分析失敗：' + (e.message || ''));
    }
    setGeneratingAio(false);
  };

  // Feature 7: Brand Persona
  const handleGeneratePersona = async (values) => {
    if (!selectedBrand) return;
    setGeneratingPersona(true);
    setPersonaResult(null);
    try {
      const res = await citationMonitorAPI.generatePersona(selectedBrand.id, values);
      setPersonaResult(res.data.persona || null);
      message.success('品牌人格已生成！');
    } catch (e) {
      message.error('生成失敗：' + (e.message || ''));
    }
    setGeneratingPersona(false);
  };

  // Feature 8: GEO Lite
  const handleGenerateGeoLite = async (values) => {
    if (!selectedBrand) return;
    setGeneratingGeoLite(true);
    setGeoLiteMap(null);
    try {
      const res = await citationMonitorAPI.generateGeoLite(selectedBrand.id, values);
      setGeoLiteMap(res.data.map_data);
      setGeoLiteMapId(res.data.map_id);
      setGeoLiteCreatedAt(res.data.created_at);
      message.success('品牌知識地圖已生成！');
    } catch (e) {
      message.error('生成失敗：' + (e.message || ''));
    }
    setGeneratingGeoLite(false);
  };

  const copyGeoLiteAsMarkdown = () => {
    if (!geoLiteMap) return;
    const lines = [];
    lines.push(`# ${geoLiteMap.brand}`);
    if (geoLiteMap.tagline) lines.push(`> ${geoLiteMap.tagline}`);
    if (geoLiteMap.market_position) lines.push(`\n${geoLiteMap.market_position}`);
    (geoLiteMap.categories || []).forEach(cat => {
      lines.push(`\n## ${cat.name}`);
      if (cat.description) lines.push(cat.description);
      (cat.products || []).forEach(p => {
        lines.push(`\n### ${p.name}`);
        if (p.description) lines.push(p.description);
        if (p.use_cases?.length) {
          lines.push('\n**應用場景：**');
          p.use_cases.forEach(u => lines.push(`- ${u}`));
        }
        if (p.faqs?.length) {
          lines.push('\n**FAQ：**');
          p.faqs.forEach(f => lines.push(`**Q: ${f.question}**\nA: ${f.answer}`));
        }
      });
    });
    if (geoLiteMap.global_faqs?.length) {
      lines.push('\n## 品牌 FAQ');
      geoLiteMap.global_faqs.forEach(f => lines.push(`**Q: ${f.question}**\nA: ${f.answer}`));
    }
    if (geoLiteMap.differentiators?.length) {
      lines.push('\n## 品牌差異化優勢');
      geoLiteMap.differentiators.forEach(d => lines.push(`- ${d}`));
    }
    navigator.clipboard.writeText(lines.join('\n'));
    message.success('已複製 Markdown！');
  };

  // Feature 9: GEO Content
  const loadGeoContent = async (brandId) => {
    try {
      const res = await citationMonitorAPI.listGeoContent(brandId);
      setGeoContents(res.data.items || []);
    } catch { setGeoContents([]); }
  };

  const handleGenerateGeoContent = async (values) => {
    if (!selectedBrand) return;
    setGeneratingGeoContent(true);
    try {
      const res = await citationMonitorAPI.generateGeoContent(selectedBrand.id, values);
      const newItems = res.data.items || [];
      setGeoContents(prev => [...newItems, ...prev]);
      message.success(`已生成 ${newItems.length} 篇 GEO 內容！`);
      geoContentForm.resetFields(['keyword', 'context']);
    } catch (e) {
      message.error('生成失敗：' + (e.message || ''));
    }
    setGeneratingGeoContent(false);
  };

  const handleDeleteGeoContent = async (id) => {
    try {
      await citationMonitorAPI.deleteGeoContent(id);
      setGeoContents(prev => prev.filter(c => c.id !== id));
      message.success('已刪除');
    } catch { message.error('刪除失敗'); }
  };

  // Feature 10: FAQ Knowledge Base
  const loadFaqs = async (brandId) => {
    try {
      const res = await citationMonitorAPI.listFaqs(brandId);
      setFaqs(res.data || []);
    } catch { setFaqs([]); }
  };

  const handleGenerateFaqs = async (values) => {
    if (!selectedBrand) return;
    setGeneratingFaqs(true);
    try {
      const res = await citationMonitorAPI.generateFaqs(selectedBrand.id, values);
      const items = res.data || [];
      setFaqs(prev => [...items, ...prev]);
      message.success(`已 AI 生成 ${items.length} 個 FAQ！`);
      faqGenForm.resetFields();
    } catch (e) {
      message.error('生成失敗：' + (e.message || ''));
    }
    setGeneratingFaqs(false);
  };

  const handleSaveFaq = async (values) => {
    if (!selectedBrand) return;
    setSavingFaq(true);
    try {
      if (editingFaq) {
        const res = await citationMonitorAPI.updateFaq(editingFaq.id, values);
        setFaqs(prev => prev.map(f => f.id === editingFaq.id ? res.data : f));
        message.success('已更新');
      } else {
        const res = await citationMonitorAPI.createFaq(selectedBrand.id, { ...values, language: 'zh-TW' });
        setFaqs(prev => [res.data, ...prev]);
        message.success('已新增');
      }
      setFaqModal(false);
      setEditingFaq(null);
      faqForm.resetFields();
    } catch { message.error('儲存失敗'); }
    setSavingFaq(false);
  };

  const handleDeleteFaq = async (id) => {
    try {
      await citationMonitorAPI.deleteFaq(id);
      setFaqs(prev => prev.filter(f => f.id !== id));
      message.success('已刪除');
    } catch { message.error('刪除失敗'); }
  };

  const handleGetJsonLd = async () => {
    if (!selectedBrand) return;
    try {
      const res = await citationMonitorAPI.getFaqJsonLd(selectedBrand.id);
      setFaqJsonLd(res.data.json_ld || '');
      message.success(`已產出 ${res.data.count} 個 FAQ JSON-LD`);
    } catch { message.error('尚無 FAQ 或產出失敗'); }
  };

  // ============================================================
  // RENDER
  if (checkingSub) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}><Text type="secondary">检查订阅状态...</Text></div>
      </div>
    );
  }



  // ============================================================


      {/* Subscription Gate Modal */}
      <Modal
        title="需要訂閱或優惠券"
        open={subGateOpen}
        onCancel={() => setSubGateOpen(false)}
        footer={null}
        width={420}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <RocketOutlined style={{ fontSize: 48, color: '#fa8c16', marginBottom: 12 }} />
          <Title level={4}>需要有效訂閱或優惠券才能使用 AI 引用監測</Title>
          <Paragraph type="secondary">
            AI 引用監測使用 AI 模型分析品牌在各大平台的引用情況，屬於進階功能。
          </Paragraph>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Button type="primary" size="large" icon={<DollarOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/billing'); }}>
              前往訂閱方案
            </Button>
            <Button size="large" icon={<GiftOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/billing'); }}>
              兌換優惠券
            </Button>
            <Button size="large" icon={<SendOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/coupon-request'); }}>
              申請免費優惠券
            </Button>
          </Space>
          <Divider />
          <Text type="secondary" style={{ fontSize: 12 }}>
            你可以在「帳務中心」查看訂閱狀態與購買方案。
          </Text>
        </div>
      </Modal>


  if (!selectedBrand) {
    return (
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        <Title level={3}><BarChartOutlined /> AI 引用監測</Title>
        <Paragraph type="secondary">
          建立品牌、設定關鍵字，自動監測 AI 平台（Claude / ChatGPT / Perplexity）對品牌的引用情形。
        </Paragraph>

        <Card
          title="品牌列表"
          extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setBrandModal(true)}>新增品牌</Button>}
        >
          {loadingBrands ? <Spin /> : (
            brands.length === 0 ? (
              <Empty description="尚無品牌，請先建立" />
            ) : (
              <Row gutter={[16, 16]}>
                {brands.map(b => (
                  <Col key={b.id} xs={24} sm={12} md={8}>
                    <Card
                      hoverable
                      onClick={() => selectBrand(b)}
                      actions={[
                        <Popconfirm title="確定刪除此品牌？" onConfirm={(e) => { e.stopPropagation(); handleDeleteBrand(b.id); }}>
                          <DeleteOutlined key="delete" onClick={(e) => e.stopPropagation()} />
                        </Popconfirm>,
                      ]}
                    >
                      <Card.Meta
                        title={b.brand_name}
                        description={
                          <Space direction="vertical" size={4}>
                            <Text type="secondary">{b.description || '尚無描述'}</Text>
                            <Space>
                              <Tag><TagOutlined /> {b.keyword_count || 0} 關鍵字</Tag>
                              <Tag><SearchOutlined /> {b.total_queries || 0} 次查詢</Tag>
                            </Space>
                          </Space>
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            )
          )}
        </Card>

        <Modal title="新增品牌" open={brandModal} onCancel={() => setBrandModal(false)}
          onOk={() => brandForm.submit()} okText="建立">
          <Form form={brandForm} layout="vertical" onFinish={handleCreateBrand}>
            <Form.Item name="brand_name" label="品牌名稱" rules={[{ required: true, message: '請輸入品牌名稱' }]}>
              <Input placeholder="例如：Comboware 宇聯科技" />
            </Form.Item>
            <Form.Item name="description" label="品牌描述">
              <Input.TextArea rows={2} placeholder="簡短描述品牌業務" />
            </Form.Item>
            <Form.Item name="logo_url" label="Logo URL">
              <Input placeholder="https://..." />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  // ---- Brand Selected: show tabs ----

  const d = dashboard;
  const kws = selectedBrand?.keywords || [];
  const wls = selectedBrand?.whitelist || [];

  const stabilityData = d?.keyword_stabilities || [];
  const competitorStats = d?.competitor_stats || {};

  const queryColumns = [
    {
      title: '關鍵字', dataIndex: 'keyword_text', key: 'kw', width: 160,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: '平台', dataIndex: 'ai_platform', key: 'plat', width: 100,
      render: (p) => {
        const colors = { claude: 'purple', perplexity: 'blue', openai: 'green', gemini: 'geekblue' };
        return <Tag color={colors[p] || 'default'}>{PLATFORM_LABELS[p] || p}</Tag>;
      },
    },
    {
      title: '引用數', dataIndex: 'citation_count', key: 'cites', width: 80,
      render: (c) => <Text strong style={{ color: c > 0 ? '#52c41a' : '#999' }}>{c}</Text>,
    },
    {
      title: '情感', dataIndex: 'sentiment_score', key: 'sent', width: 100,
      render: (s) => s ? (
        <Tag color={sentimentColor(s)}>{s.toFixed(1)} {sentimentLabel(s)}</Tag>
      ) : '-',
    },
    {
      title: '能見度', dataIndex: 'visibility_score', key: 'vis', width: 80,
      render: (v) => v ? <Text strong>{v.toFixed(1)}</Text> : '-',
    },
    {
      title: '狀態', dataIndex: 'status', key: 'status', width: 80,
      render: (s) => <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'orange'}>{s}</Tag>,
    },
    {
      title: '時間', dataIndex: 'queried_at', key: 'time', width: 160,
      render: (t) => t ? new Date(t).toLocaleString('zh-TW') : '-',
    },
    {
      title: '', key: 'action', width: 50,
      render: (_, r) => <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewQuery(r.id)} />,
    },
  ];

  const priorityColor = { high: 'red', medium: 'orange', low: 'blue' };
  const intentTypeColor = { Why: 'purple', How: 'green', What: 'blue', Compare: 'orange' };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={() => { setSelectedBrand(null); setDashboard(null); }}>
          &larr; 返回品牌列表
        </Button>
        <Title level={4} style={{ margin: 0 }}>{selectedBrand.brand_name}</Title>
        <Text type="secondary">{selectedBrand.description}</Text>
      </Space>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* === Dashboard Tab === */}
        <TabPane tab={<span><BarChartOutlined /> 總覽</span>} key="dashboard">
          {loadingDashboard ? <Spin size="large" style={{ display: 'block', margin: '60px auto' }} /> : !d ? (
            <Empty description="尚無監測數據，請先執行監測" />
          ) : (
            <>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                  <Card><Statistic title="整體能見度" value={d.overall_visibility} precision={1} suffix="分" /></Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card><Statistic title="本月引用數" value={d.total_citations_month} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card><Statistic title="平均情感" value={d.avg_sentiment || 0} precision={1} suffix="/ 5" valueStyle={{ color: sentimentColor(d.avg_sentiment) }} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card><Statistic title="監測平台" value={d.platform_count} suffix="個" /></Card>
                </Col>
              </Row>

              {/* Platform Stats */}
              {Object.keys(d.platform_stats || {}).length > 0 && (
                <Card title="各平台統計" size="small" style={{ marginBottom: 24 }}>
                  <Row gutter={16}>
                    {Object.entries(d.platform_stats).map(([plat, stats]) => (
                      <Col key={plat} xs={24} sm={8}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <Tag color={plat === 'claude' ? 'purple' : plat === 'perplexity' ? 'blue' : 'green'} style={{ fontSize: 14, fontWeight: 600 }}>
                            {PLATFORM_LABELS[plat] || plat}
                          </Tag>
                          <div style={{ marginTop: 8 }}>
                            <Text>查詢 {stats.query_count} 次</Text>
                            <Divider type="vertical" />
                            <Text>引用 {stats.citation_count} 筆</Text>
                            {stats.avg_sentiment && (
                              <>
                                <Divider type="vertical" />
                                <Text style={{ color: sentimentColor(stats.avg_sentiment) }}>
                                  情感 {stats.avg_sentiment.toFixed(1)}
                                </Text>
                              </>
                            )}
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}

              {/* Feature 2: Competitor Stats */}
              {Object.keys(competitorStats).length > 0 && (
                <Card title={<Space><TeamOutlined /> 競品引用對比</Space>} size="small" style={{ marginBottom: 24 }}>
                  <Row gutter={16}>
                    {Object.entries(competitorStats).map(([comp, stats]) => {
                      const mentionRate = stats.queries_checked > 0
                        ? ((stats.mentions / stats.queries_checked) * 100).toFixed(0)
                        : 0;
                      return (
                        <Col key={comp} xs={24} sm={12} md={8}>
                          <Card size="small" style={{ textAlign: 'center' }}>
                            <Text strong>{comp}</Text>
                            <div style={{ marginTop: 8 }}>
                              <Statistic
                                value={stats.mentions}
                                suffix={`/ ${stats.queries_checked} 次查詢`}
                                valueStyle={{ fontSize: 20, color: stats.mentions > 0 ? '#fa8c16' : '#52c41a' }}
                              />
                              <Tag color={stats.mentions > 0 ? 'orange' : 'green'} style={{ marginTop: 4 }}>
                                提及率 {mentionRate}%
                              </Tag>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    * 統計過去 30 天的 AI 回覆中競品被提及的次數
                  </Text>
                </Card>
              )}

              {/* Keyword Stability */}
              <Card title="關鍵字穩定度" size="small" style={{ marginBottom: 24 }}>
                {stabilityData.length === 0 ? <Empty description="尚無數據" /> : (
                  <Table
                    dataSource={stabilityData}
                    rowKey="keyword_id"
                    size="small"
                    pagination={false}
                    columns={[
                      { title: '關鍵字', dataIndex: 'keyword_text', key: 'kw', render: (t) => <Text strong>{t}</Text> },
                      {
                        title: '類別', dataIndex: 'keyword_category', key: 'cat', width: 100,
                        render: (c) => <Tag>{CATEGORY_LABELS[c] || c}</Tag>,
                      },
                      {
                        title: '穩定度', dataIndex: 'stability_grade', key: 'grade', width: 120,
                        render: (g) => {
                          const cfg = STABILITY_CONFIG[g] || STABILITY_CONFIG.never;
                          return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
                        },
                      },
                      {
                        title: '4週引用', dataIndex: 'total_citations_4w', key: 'total', width: 100,
                        render: (v) => <Text strong>{v}</Text>,
                      },
                      {
                        title: '趨勢', dataIndex: 'citation_trend', key: 'trend', width: 140,
                        render: (arr) => arr ? (
                          <Space size={2}>
                            {arr.map((v, i) => (
                              <div key={i} style={{
                                width: 28, height: Math.max(4, v * 8), minHeight: 4,
                                background: v > 0 ? '#1890ff' : '#f0f0f0',
                                borderRadius: 2, display: 'inline-block', verticalAlign: 'bottom',
                              }} title={`第${i + 1}週: ${v}`} />
                            ))}
                          </Space>
                        ) : '-',
                      },
                      {
                        title: '情感', dataIndex: 'avg_sentiment', key: 'sent', width: 80,
                        render: (s) => s ? <Text style={{ color: sentimentColor(s) }}>{s.toFixed(1)}</Text> : '-',
                      },
                    ]}
                  />
                )}
              </Card>

              {/* Top Cited URLs */}
              {(d.top_cited_urls || []).length > 0 && (
                <Card title="最常被引用的 URL" size="small" style={{ marginBottom: 24 }}>
                  <List
                    size="small"
                    dataSource={d.top_cited_urls}
                    renderItem={(item, idx) => (
                      <List.Item>
                        <Space>
                          <Badge count={idx + 1} style={{ backgroundColor: idx < 3 ? '#1890ff' : '#999' }} />
                          <LinkOutlined />
                          <Text copyable style={{ fontSize: 13 }}>{item.url}</Text>
                        </Space>
                        <Tag>{item.count} 次</Tag>
                      </List.Item>
                    )}
                  />
                </Card>
              )}
            </>
          )}
        </TabPane>

        {/* === Monitor Tab === */}
        <TabPane tab={<span><PlayCircleOutlined /> 執行監測</span>} key="monitor">
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>選擇要查詢的 AI 平台：</Text>
              <Select
                mode="multiple"
                value={runPlatforms}
                onChange={setRunPlatforms}
                style={{ width: '100%', maxWidth: 400 }}
                options={[
                  { label: 'Claude (Anthropic)', value: 'claude' },
                  { label: 'Perplexity AI', value: 'perplexity' },
                  { label: 'ChatGPT (OpenAI)', value: 'openai' },
                  { label: 'Google Gemini', value: 'gemini' },
                ]}
              />
              <Text type="secondary">
                將使用品牌下所有啟用的關鍵字向選定的 AI 平台查詢，並分析引用情形和情感。
                每個關鍵字×每個平台 = 一次查詢，約需 10-30 秒。
              </Text>
              {runPlatforms.includes('gemini') && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 4 }}
                  message="Gemini 免費方案限制"
                  description={
                    <span>
                      Google AI Studio 免費方案每分鐘限 15 次、每天 1,500 次請求。若出現 failed，請前往{' '}
                      <a href="https://console.cloud.google.com/billing" target="_blank" rel="noreferrer">
                        Google Cloud Billing
                      </a>{' '}
                      為專案啟用帳單（gemini-2.0-flash 費用極低，約 $0.075/1M tokens）。
                    </span>
                  }
                />
              )}
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                loading={running}
                onClick={handleRunMonitor}
                disabled={kws.length === 0}
              >
                {running ? '監測中...' : '開始監測'}
              </Button>
              {kws.length === 0 && (
                <Text type="warning">請先在「設定」頁籤新增關鍵字</Text>
              )}
            </Space>
          </Card>

          <Card title={<Space>查詢紀錄 <Button size="small" icon={<ReloadOutlined />} onClick={() => loadQueries(selectedBrand.id)} /></Space>}>
            <Table
              columns={queryColumns}
              dataSource={queries}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 15 }}
              locale={{ emptyText: '尚無查詢紀錄' }}
            />
          </Card>
        </TabPane>

        {/* === Settings Tab === */}
        <TabPane tab={<span><TagOutlined /> 設定</span>} key="settings">
          {/* Keywords */}
          <Card
            title={`關鍵字 (${kws.length})`}
            extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setKeywordModal(true)}>新增</Button>}
            style={{ marginBottom: 24 }}
          >
            {kws.length === 0 ? <Empty description="尚無關鍵字" /> : (
              <Table
                dataSource={kws}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: '關鍵字', dataIndex: 'keyword_text', key: 'kw', render: (t) => <Text strong>{t}</Text> },
                  {
                    title: '類別', dataIndex: 'keyword_category', key: 'cat', width: 120,
                    render: (c) => <Tag>{CATEGORY_LABELS[c] || c}</Tag>,
                  },
                  { title: '語言', dataIndex: 'language', key: 'lang', width: 80 },
                  {
                    title: '狀態', dataIndex: 'is_active', key: 'active', width: 80,
                    render: (a) => <Tag color={a ? 'green' : 'default'}>{a ? '啟用' : '停用'}</Tag>,
                  },
                  {
                    title: '', key: 'action', width: 50,
                    render: (_, r) => (
                      <Popconfirm title="確定刪除？" onConfirm={() => handleDeleteKeyword(r.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            )}
          </Card>

          {/* Whitelist */}
          <Card
            title={`URL 白名單 (${wls.length})`}
            extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setWhitelistModal(true)}>新增</Button>}
            style={{ marginBottom: 24 }}
          >
            {wls.length === 0 ? <Empty description="尚無白名單" /> : (
              <Table
                dataSource={wls}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: '網域模式', dataIndex: 'domain_pattern', key: 'domain', render: (d) => <Text code>{d}</Text> },
                  { title: '頁面類型', dataIndex: 'page_type', key: 'type', width: 120 },
                  {
                    title: '', key: 'action', width: 50,
                    render: (_, r) => (
                      <Popconfirm title="確定刪除？" onConfirm={() => handleDeleteWhitelist(r.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            )}
          </Card>

          {/* Feature 1: Auto-Schedule Settings */}
          <Card
            title={<Space><CalendarOutlined /> 自動排程設定</Space>}
            style={{ marginBottom: 24 }}
          >
            <Alert
              message="週自動監測排程"
              description="設定後，系統每小時會檢查是否到達指定的星期幾，自動執行監測。月報於每月 1 日自動生成。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form form={scheduleForm} layout="vertical" onFinish={handleSaveSchedule}>
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item name="auto_monitor_enabled" label="啟用週自動監測" valuePropName="checked">
                    <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="auto_monitor_day" label="監測星期幾">
                    <Select options={WEEKDAY_LABELS.map((l, i) => ({ value: i, label: l }))} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="auto_monitor_platforms" label="自動監測平台">
                <Select
                  mode="multiple"
                  options={[
                    { label: 'Claude', value: 'claude' },
                    { label: 'Perplexity', value: 'perplexity' },
                    { label: 'ChatGPT', value: 'openai' },
                    { label: 'Gemini', value: 'gemini' },
                  ]}
                />
              </Form.Item>
              <Divider />
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item name="auto_report_enabled" label="啟用月自動報告" valuePropName="checked">
                    <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="industry" label="品牌產業（用於月報生成）" extra="例如：台灣 B2B 軟體廠商，提供 HCI 超融合基礎架構">
                <Input placeholder="請輸入產業描述" />
              </Form.Item>
              <Form.Item
                name="competitors"
                label="競品名稱（每行一個）"
                extra="例如：Grundfos（用於競品引用對比分析）"
              >
                <Input.TextArea rows={4} placeholder={'Grundfos\nXylem\nFlowserve'} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={savingSchedule} icon={<CalendarOutlined />}>
                儲存排程設定
              </Button>
            </Form>
          </Card>
        </TabPane>

        {/* Reports Tab */}
        <TabPane tab={<span><FileTextOutlined /> 建議方案報告</span>} key="reports">
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => setReportModal(true)}
            >
              生成建議方案報告
            </Button>
          </Space>
          <Alert
            message="AI 品牌能見度提升建議方案"
            description="系統將根據所有監測數據，自動分析每個關鍵字的穩定度，並透過 Claude AI 生成個人化行動建議，輸出 Word 報告（5個章節：品牌現況摘要、關鍵字穩定度分析、優先行動建議、內容佈局策略、執行時間表）。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Spin spinning={loadingReports}>
            {reports.length === 0 ? (
              <Empty description="尚未生成任何報告" />
            ) : (
              <Table
                dataSource={reports}
                rowKey="id"
                size="small"
                columns={[
                  {
                    title: '產業',
                    dataIndex: 'industry',
                    key: 'industry',
                  },
                  {
                    title: '監測次數',
                    dataIndex: 'monitor_count',
                    key: 'monitor_count',
                    width: 90,
                    align: 'center',
                  },
                  {
                    title: '狀態',
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: (s) => {
                      const cfg = {
                        done: { color: 'success', label: '完成' },
                        generating: { color: 'processing', label: '生成中' },
                        failed: { color: 'error', label: '失敗' },
                        pending: { color: 'default', label: '待處理' },
                      }[s] || { color: 'default', label: s };
                      return <Tag color={cfg.color}>{cfg.label}</Tag>;
                    },
                  },
                  {
                    title: '生成時間',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    render: (t) => t ? new Date(t).toLocaleString('zh-TW') : '-',
                  },
                  {
                    title: '操作',
                    key: 'actions',
                    width: 120,
                    render: (_, record) => (
                      <Space>
                        {record.status === 'done' && (
                          <Tooltip title="下載 Word 報告">
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => citationMonitorAPI.downloadReport(record.id)}
                            >
                              下載
                            </Button>
                          </Tooltip>
                        )}
                        {record.status === 'failed' && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {record.error_message?.slice(0, 30)}…
                          </Text>
                        )}
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </Spin>
        </TabPane>

        {/* Feature 3: Stability History Tab */}
        <TabPane
          tab={<span><HistoryOutlined /> 穩定度趨勢</span>}
          key="stability-history"
          onTabClick={() => loadStabilityHistory(selectedBrand.id)}
        >
          <Button
            icon={<ReloadOutlined />}
            style={{ marginBottom: 16 }}
            onClick={() => loadStabilityHistory(selectedBrand.id)}
          >
            重新載入
          </Button>
          <Alert
            message="關鍵字穩定度趨勢"
            description="每次執行監測後，系統會自動記錄每個關鍵字的穩定度快照，讓您追蹤長期趨勢變化。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Spin spinning={loadingHistory}>
            {stabilityHistory.length === 0 ? (
              <Empty description="尚無歷史數據，請先執行幾次監測" />
            ) : (
              stabilityHistory.map(kw => (
                kw.history.length === 0 ? null : (
                  <Card
                    key={kw.keyword_id}
                    title={<Space><TagOutlined /><Text strong>{kw.keyword_text}</Text></Space>}
                    size="small"
                    style={{ marginBottom: 16 }}
                  >
                    <Table
                      dataSource={kw.history}
                      rowKey="snapshot_date"
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '日期', dataIndex: 'snapshot_date', key: 'date', width: 120 },
                        {
                          title: '穩定度', dataIndex: 'stability_grade', key: 'grade', width: 120,
                          render: (g) => {
                            const cfg = STABILITY_CONFIG[g] || STABILITY_CONFIG.never;
                            return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
                          },
                        },
                        {
                          title: '引用率', dataIndex: 'citation_rate', key: 'rate', width: 100,
                          render: (v) => v != null ? <Text>{v.toFixed(2)} /週</Text> : '-',
                        },
                        {
                          title: '平均情感', dataIndex: 'avg_score', key: 'score', width: 100,
                          render: (s) => s ? <Text style={{ color: sentimentColor(s) }}>{s.toFixed(1)}</Text> : '-',
                        },
                      ]}
                    />
                  </Card>
                )
              ))
            )}
          </Spin>
        </TabPane>

        {/* Feature 4+5: llms.txt & Schema Tab */}
        <TabPane tab={<span><CodeOutlined /> llms.txt & Schema</span>} key="content-tools">
          <Row gutter={[24, 24]}>
            {/* llms.txt Generator */}
            <Col xs={24} lg={12}>
              <Card
                title={<Space><FileTextOutlined /> llms.txt 生成器</Space>}
                style={{ height: '100%' }}
              >
                <Alert
                  message="llms.txt 讓 AI 模型了解您的品牌"
                  description="生成符合 llms.txt 規範的文件，放置在網站根目錄，幫助 AI 正確理解並引用您的品牌。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form form={llmsForm} layout="vertical" onFinish={handleGenerateLlms}>
                  <Form.Item
                    name="brand_description"
                    label="品牌描述"
                    rules={[{ required: true, message: '請輸入品牌描述' }]}
                  >
                    <Input.TextArea rows={3} placeholder="詳細描述品牌的業務、定位和主要產品" />
                  </Form.Item>
                  <Form.Item name="products" label="主要產品/服務">
                    <Input.TextArea rows={2} placeholder="列出主要產品或服務" />
                  </Form.Item>
                  <Form.Item name="use_cases" label="主要應用場景">
                    <Input placeholder="例如：製造業 MES 系統、工業物聯網" />
                  </Form.Item>
                  <Form.Item name="certifications" label="認證與獎項">
                    <Input placeholder="例如：ISO 9001、台灣精品獎" />
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={generatingLlms}
                    icon={<RobotOutlined />}
                  >
                    {generatingLlms ? '生成中...' : '生成 llms.txt'}
                  </Button>
                </Form>

                {llmsTxtResult && (
                  <div style={{ marginTop: 16 }}>
                    <Space style={{ marginBottom: 8 }}>
                      <Text strong>生成結果：</Text>
                      <CopyButton text={llmsTxtResult} />
                    </Space>
                    <div style={{
                      background: '#f5f5f5',
                      padding: 12,
                      borderRadius: 6,
                      maxHeight: 300,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                    }}>
                      {llmsTxtResult}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      將此內容儲存為 llms.txt，放置在網站根目錄（例：https://yourdomain.com/llms.txt）
                    </Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Schema Markup Generator */}
            <Col xs={24} lg={12}>
              <Card
                title={<Space><CodeOutlined /> Schema 標記生成器</Space>}
                style={{ height: '100%' }}
              >
                <Alert
                  message="結構化資料幫助 AI 理解您的品牌"
                  description="生成 JSON-LD Schema.org 標記，嵌入網頁頭部，讓 AI 搜尋引擎更準確地理解並引用您的品牌。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form
                  form={schemaForm}
                  layout="vertical"
                  onFinish={handleGenerateSchema}
                  initialValues={{
                    schema_type: 'Organization',
                    brand_name: selectedBrand?.brand_name || '',
                    description: selectedBrand?.description || '',
                  }}
                >
                  <Form.Item name="schema_type" label="Schema 類型">
                    <Select options={[
                      { value: 'Organization', label: 'Organization（組織）' },
                      { value: 'Product', label: 'Product（產品）' },
                      { value: 'FAQ', label: 'FAQ（常見問題）' },
                    ]} />
                  </Form.Item>
                  <Form.Item name="brand_name" label="品牌/產品名稱" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="description" label="描述" rules={[{ required: true }]}>
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="url" label="網站 URL" rules={[{ required: true, message: '請輸入網站 URL' }]}>
                    <Input placeholder="https://www.yourbrand.com" />
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={generatingSchema}
                    icon={<CodeOutlined />}
                  >
                    {generatingSchema ? '生成中...' : '生成 Schema 標記'}
                  </Button>
                </Form>

                {schemaResult && (
                  <div style={{ marginTop: 16 }}>
                    <Space style={{ marginBottom: 8 }}>
                      <Text strong>生成結果：</Text>
                      <CopyButton text={schemaResult} />
                    </Space>
                    <div style={{
                      background: '#001529',
                      color: '#52c41a',
                      padding: 12,
                      borderRadius: 6,
                      maxHeight: 300,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                    }}>
                      {schemaResult}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      將此程式碼貼入網頁的 &lt;head&gt; 標籤中
                    </Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Feature 6+7: AIO Analysis & Brand Persona Tab */}
        <TabPane tab={<span><BulbOutlined /> AIO + 品牌人格</span>} key="aio-persona">
          <Row gutter={[24, 24]}>
            {/* AIO Analysis */}
            <Col xs={24}>
              <Card title={<Space><SearchOutlined /> AIO 搜尋意圖分析</Space>}>
                <Alert
                  message="AI Overview (AIO) 搜尋意圖分析"
                  description="分析潛在客戶在 AI 搜尋引擎中的搜尋意圖，幫助您優化內容策略，提升在 AI 生成摘要中的被引用機率。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form form={aioForm} layout="inline" onFinish={handleAioAnalysis} initialValues={{ market: 'TW', count: 60 }}>
                  <Form.Item name="market" label="市場">
                    <Select
                      style={{ width: 160 }}
                      options={[
                        { value: 'TW', label: '台灣（繁體中文）' },
                        { value: 'US', label: '美國（English）' },
                      ]}
                      onChange={(v) => aioForm.setFieldValue('count', v === 'TW' ? 60 : 90)}
                    />
                  </Form.Item>
                  <Form.Item name="count" label="生成數量">
                    <InputNumber min={10} max={120} />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={generatingAio}
                      icon={<RobotOutlined />}
                    >
                      {generatingAio ? '分析中...' : '開始 AIO 分析'}
                    </Button>
                  </Form.Item>
                </Form>

                {aioIntents.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      共 {aioIntents.length} 個搜尋意圖群組：
                    </Text>
                    <Table
                      dataSource={aioIntents}
                      rowKey={(_, i) => i}
                      size="small"
                      pagination={{ pageSize: 15 }}
                      columns={[
                        {
                          title: '搜尋意圖', dataIndex: 'intent', key: 'intent',
                          render: (t) => <Text>{t}</Text>,
                        },
                        {
                          title: '類型', dataIndex: 'type', key: 'type', width: 90,
                          render: (t) => <Tag color={intentTypeColor[t] || 'default'}>{t}</Tag>,
                        },
                        {
                          title: '優先度', dataIndex: 'priority', key: 'priority', width: 90,
                          render: (p) => <Tag color={priorityColor[p] || 'default'}>{p === 'high' ? '高' : p === 'medium' ? '中' : '低'}</Tag>,
                        },
                        {
                          title: '建議關鍵字', dataIndex: 'suggested_keyword', key: 'kw',
                          render: (k) => <Tag icon={<TagOutlined />}>{k}</Tag>,
                        },
                      ]}
                    />
                  </div>
                )}
              </Card>
            </Col>

            {/* Brand Persona */}
            <Col xs={24}>
              <Card title={<Space><UserOutlined /> Brand Persona 品牌人格</Space>}>
                <Alert
                  message="品牌人格設定讓 AI 引用更準確"
                  description="定義品牌在 AI 時代的語氣規範和核心訊息，確保 AI 平台引用時能呈現正確的品牌形象。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form
                  form={personaForm}
                  layout="vertical"
                  onFinish={handleGeneratePersona}
                  initialValues={{
                    industry: selectedBrand?.industry || '',
                    competitors: (selectedBrand?.competitors || []).join('、'),
                  }}
                >
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="industry" label="產業描述" rules={[{ required: true, message: '請輸入產業' }]}>
                        <Input placeholder="例如：台灣 B2B 軟體，HCI 超融合架構" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="target_audience" label="目標受眾" rules={[{ required: true }]}>
                        <Input placeholder="例如：企業 IT 決策者、CTO、資訊長" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="brand_values" label="品牌價值主張" rules={[{ required: true }]}>
                    <Input.TextArea rows={2} placeholder="例如：技術創新、在地支援、高可靠性、成本效益" />
                  </Form.Item>
                  <Form.Item name="competitors" label="主要競品（可選）">
                    <Input placeholder="例如：VMware、Nutanix、HPE" />
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={generatingPersona}
                    icon={<UserOutlined />}
                  >
                    {generatingPersona ? '生成中...' : '生成品牌人格'}
                  </Button>
                </Form>

                {personaResult && (
                  <div style={{ marginTop: 24 }}>
                    <Divider>品牌人格文件</Divider>
                    <Card
                      size="small"
                      style={{ background: 'linear-gradient(135deg, #667eea22, #764ba222)', borderRadius: 8, marginBottom: 16 }}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>品牌主張</Text>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1890ff', marginTop: 4 }}>
                        "{personaResult.core_message}"
                      </div>
                    </Card>

                    <Card size="small" title="語氣規範" style={{ marginBottom: 16 }}>
                      <Paragraph>{personaResult.tone}</Paragraph>
                    </Card>

                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Card
                          size="small"
                          title={<Text style={{ color: '#52c41a' }}><CheckCircleOutlined /> AI 引用建議做 (Do)</Text>}
                        >
                          <List
                            dataSource={personaResult.dos || []}
                            renderItem={(item, i) => (
                              <List.Item style={{ padding: '4px 0' }}>
                                <Space>
                                  <Badge count={i + 1} style={{ backgroundColor: '#52c41a' }} />
                                  <Text>{item}</Text>
                                </Space>
                              </List.Item>
                            )}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Card
                          size="small"
                          title={<Text style={{ color: '#f5222d' }}><CloseCircleOutlined /> AI 引用禁忌 (Don't)</Text>}
                        >
                          <List
                            dataSource={personaResult.donts || []}
                            renderItem={(item, i) => (
                              <List.Item style={{ padding: '4px 0' }}>
                                <Space>
                                  <Badge count={i + 1} style={{ backgroundColor: '#f5222d' }} />
                                  <Text>{item}</Text>
                                </Space>
                              </List.Item>
                            )}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Feature 8: GEO Lite Tab */}
        <TabPane tab={<span><ApartmentOutlined /> GEO Lite 知識地圖</span>} key="geo-lite">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Card title={<Space><NodeIndexOutlined /> 生成品牌知識地圖</Space>}>
                <Alert
                  message="GEO Lite — 階層式品牌知識地圖"
                  description="建立品牌 → 產品 → 應用場景 → FAQ 的完整知識架構，提升 Claude 結構化引用與 Perplexity 比較型引用精度。"
                  type="info" showIcon style={{ marginBottom: 16 }}
                />
                <Form form={geoLiteForm} layout="vertical" onFinish={handleGenerateGeoLite}>
                  <Form.Item name="industry" label="產業類別" rules={[{ required: true, message: '請填寫產業' }]}>
                    <Input placeholder="例：工業幫浦、SaaS 軟體、醫療設備" />
                  </Form.Item>
                  <Form.Item name="products" label="主要產品/服務">
                    <Input.TextArea rows={3} placeholder="例：離心泵、潛水泵、污水處理設備（每行一項或逗號分隔）" />
                  </Form.Item>
                  <Form.Item name="target_audience" label="目標受眾">
                    <Input placeholder="例：工程師、採購主管、建設公司" />
                  </Form.Item>
                  <Form.Item name="competitors" label="主要競品">
                    <Input placeholder="例：Grundfos, Xylem, Goulds" />
                  </Form.Item>
                  <Form.Item name="market" label="市場語言" initialValue="TW">
                    <Select>
                      <Select.Option value="TW">台灣（繁體中文）</Select.Option>
                      <Select.Option value="US">United States (English)</Select.Option>
                    </Select>
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={generatingGeoLite} block icon={<ThunderboltOutlined />}>
                    {generatingGeoLite ? '生成中… (約60秒)' : '生成知識地圖'}
                  </Button>
                </Form>
              </Card>
            </Col>
            <Col xs={24} md={16}>
              <Card
                title={<Space><ApartmentOutlined /> 品牌知識地圖</Space>}
                extra={geoLiteMap && (
                  <Space>
                    {geoLiteCreatedAt && <Text type="secondary" style={{ fontSize: 12 }}>更新：{new Date(geoLiteCreatedAt).toLocaleDateString()}</Text>}
                    <Button size="small" icon={<CopyOutlined />} onClick={copyGeoLiteAsMarkdown}>複製 Markdown</Button>
                    <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(JSON.stringify(geoLiteMap, null, 2)); message.success('已複製 JSON！'); }}>複製 JSON</Button>
                  </Space>
                )}
              >
                {!geoLiteMap ? (
                  <Empty description="尚未生成知識地圖，請填寫左側表單並生成" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <div>
                    <div style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2C5F8A 100%)', borderRadius: 8, padding: '16px 20px', marginBottom: 16, color: '#fff' }}>
                      <Title level={4} style={{ color: '#fff', margin: 0 }}>{geoLiteMap.brand}</Title>
                      {geoLiteMap.tagline && <Text style={{ color: '#b3d4f5', fontSize: 13 }}>{geoLiteMap.tagline}</Text>}
                      {geoLiteMap.market_position && <Paragraph style={{ color: '#dde8f5', fontSize: 12, marginTop: 8, marginBottom: 0 }}>{geoLiteMap.market_position}</Paragraph>}
                    </div>

                    {geoLiteMap.differentiators?.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <Text strong>品牌差異化優勢</Text>
                        <div style={{ marginTop: 6 }}>
                          {geoLiteMap.differentiators.map((d, i) => <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{d}</Tag>)}
                        </div>
                      </div>
                    )}

                    {(geoLiteMap.categories || []).map((cat, ci) => (
                      <Card key={ci} size="small" style={{ marginBottom: 12 }}
                        title={<Text strong style={{ color: '#1890ff' }}>{cat.name}</Text>}
                      >
                        {cat.description && <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>{cat.description}</Paragraph>}
                        {(cat.products || []).map((prod, pi) => (
                          <div key={pi} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                            <Text strong>{prod.name}</Text>
                            {prod.description && <Paragraph style={{ fontSize: 12, color: '#555', margin: '4px 0 8px' }}>{prod.description}</Paragraph>}
                            {prod.use_cases?.length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>應用場景：</Text>
                                <div>{prod.use_cases.map((u, i) => <Tag key={i} size="small" color="green" style={{ marginTop: 4 }}>{u}</Tag>)}</div>
                              </div>
                            )}
                            {prod.faqs?.length > 0 && (
                              <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>FAQ：</Text>
                                {prod.faqs.map((f, fi) => (
                                  <div key={fi} style={{ background: '#fafafa', borderRadius: 4, padding: '6px 8px', marginTop: 4, fontSize: 12 }}>
                                    <Text strong>Q: {f.question}</Text>
                                    <br />
                                    <Text type="secondary">A: {f.answer}</Text>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </Card>
                    ))}

                    {geoLiteMap.global_faqs?.length > 0 && (
                      <Card size="small" title="品牌 FAQ" style={{ marginBottom: 12 }}>
                        {geoLiteMap.global_faqs.map((f, i) => (
                          <div key={i} style={{ background: '#f6ffed', borderRadius: 4, padding: '6px 10px', marginBottom: 6, fontSize: 12 }}>
                            <Text strong>Q: {f.question}</Text><br />
                            <Text>A: {f.answer}</Text>
                          </div>
                        ))}
                      </Card>
                    )}
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Feature 9: GEO Content Tab */}
        <TabPane tab={<span><EditOutlined /> GEO 內容建置</span>} key="geo-content">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Card title={<Space><ThunderboltOutlined /> 生成 GEO 內容</Space>}>
                <Alert
                  message="依監測缺口產出 AI 優化內容"
                  description={<span>• <Text strong>Perplexity 型</Text>：數據/統計/比較文章<br />• <Text strong>ChatGPT 型</Text>：How-to 教學步驟指南</span>}
                  type="info" showIcon style={{ marginBottom: 16 }}
                />
                <Form form={geoContentForm} layout="vertical" onFinish={handleGenerateGeoContent}>
                  <Form.Item name="keyword" label="目標關鍵字 / 引用缺口" rules={[{ required: true, message: '請填寫關鍵字' }]}>
                    <Input placeholder="例：工業幫浦選型、污水處理解決方案" />
                  </Form.Item>
                  <Form.Item name="content_type" label="內容類型" initialValue="both">
                    <Select>
                      <Select.Option value="both">兩種都生成（Perplexity + ChatGPT）</Select.Option>
                      <Select.Option value="perplexity_data">Perplexity 數據型</Select.Option>
                      <Select.Option value="chatgpt_howto">ChatGPT How-to 型</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="language" label="語言" initialValue="zh-TW">
                    <Select>
                      <Select.Option value="zh-TW">繁體中文（台灣）</Select.Option>
                      <Select.Option value="en">English (US)</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="context" label="補充背景（選填）">
                    <Input.TextArea rows={3} placeholder="例：競品近期推出新產品，需強調我們的優勢..." />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={generatingGeoContent} block icon={<EditOutlined />}>
                    {generatingGeoContent ? '生成中… (約90秒)' : '開始生成'}
                  </Button>
                </Form>
              </Card>
            </Col>
            <Col xs={24} md={16}>
              <Card
                title={<Space><GlobalOutlined /> GEO 內容庫</Space>}
                extra={<Text type="secondary">{geoContents.length} 篇</Text>}
              >
                {geoContents.length === 0 ? (
                  <Empty description="尚無生成內容，請填寫左側表單並生成" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <List
                    dataSource={geoContents}
                    renderItem={(item) => (
                      <List.Item
                        key={item.id}
                        actions={[
                          <Button size="small" icon={<EyeOutlined />} onClick={() => setExpandedContent(expandedContent?.id === item.id ? null : item)}>
                            {expandedContent?.id === item.id ? '收起' : '查看'}
                          </Button>,
                          <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(item.content); message.success('已複製'); }}>複製</Button>,
                          <Popconfirm title="確定刪除？" onConfirm={() => handleDeleteGeoContent(item.id)}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Tag color={item.content_type === 'perplexity_data' ? 'purple' : 'orange'} style={{ marginTop: 4 }}>
                              {item.content_type === 'perplexity_data' ? 'Perplexity' : 'ChatGPT'}
                            </Tag>
                          }
                          title={<Text strong style={{ fontSize: 13 }}>{item.title}</Text>}
                          description={
                            <Space size={4}>
                              <Tag size="small">{item.keyword}</Tag>
                              <Tag size="small" color={item.language === 'zh-TW' ? 'blue' : 'green'}>{item.language === 'zh-TW' ? '繁中' : 'EN'}</Tag>
                              <Text type="secondary" style={{ fontSize: 11 }}>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</Text>
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
                {expandedContent && (
                  <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text strong>{expandedContent.title}</Text>
                      <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(expandedContent.content); message.success('已複製'); }}>複製全文</Button>
                    </div>
                    <div style={{ background: '#fafafa', borderRadius: 6, padding: 16, maxHeight: 500, overflow: 'auto', fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                      {expandedContent.content}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Feature 10: FAQ Knowledge Base Tab */}
        <TabPane tab={<span><FileTextOutlined /> FAQ 知識庫</span>} key="faq">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Card title={<Space><RobotOutlined /> AI 自動生成 FAQ</Space>} style={{ marginBottom: 16 }}>
                <Alert
                  message="結構化 FAQ 知識庫"
                  description="生成符合 Schema.org FAQPage 規範的問答，讓 AI 平台直接引用你的品牌知識。"
                  type="info" showIcon style={{ marginBottom: 12 }}
                />
                <Form form={faqGenForm} layout="vertical" onFinish={handleGenerateFaqs}>
                  <Form.Item name="industry" label="產業補充">
                    <Input placeholder="例：工業幫浦、SaaS 軟體" />
                  </Form.Item>
                  <Form.Item name="language" label="語言" initialValue="zh-TW">
                    <Select>
                      <Select.Option value="zh-TW">繁體中文</Select.Option>
                      <Select.Option value="en">English</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="count" label="生成數量" initialValue={10}>
                    <InputNumber min={5} max={30} />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={generatingFaqs} block icon={<RobotOutlined />}>
                    {generatingFaqs ? 'AI 生成中…' : 'AI 自動生成 FAQ'}
                  </Button>
                </Form>
              </Card>
              <Card title="手動新增 FAQ" extra={
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { setEditingFaq(null); faqForm.resetFields(); setFaqModal(true); }}>新增</Button>
              }>
                <Text type="secondary" style={{ fontSize: 12 }}>可手動新增、編輯特定問答內容</Text>
              </Card>
            </Col>
            <Col xs={24} md={16}>
              <Card
                title={<Space><FileTextOutlined /> FAQ 問答庫</Space>}
                extra={
                  <Space>
                    <Text type="secondary">{faqs.length} 個問答</Text>
                    <Button size="small" icon={<CodeOutlined />} onClick={handleGetJsonLd} disabled={faqs.length === 0}>匯出 JSON-LD</Button>
                  </Space>
                }
              >
                {faqs.length === 0 ? (
                  <Empty description="尚無 FAQ，請使用左側 AI 生成或手動新增" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <List
                    dataSource={faqs}
                    renderItem={(item, idx) => (
                      <List.Item
                        key={item.id}
                        actions={[
                          <Button size="small" icon={<CopyOutlined />} onClick={() => {
                            navigator.clipboard.writeText(`Q: ${item.question}\nA: ${item.answer}`);
                            message.success('已複製問答');
                          }}>複製</Button>,
                          <Button size="small" icon={<EditOutlined />} onClick={() => {
                            setEditingFaq(item);
                            faqForm.setFieldsValue({ question: item.question, answer: item.answer, category: item.category });
                            setFaqModal(true);
                          }}>編輯</Button>,
                          <Popconfirm title="確定刪除？" onConfirm={() => handleDeleteFaq(item.id)}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Badge count={idx + 1} style={{ backgroundColor: '#1890ff' }} />}
                          title={
                            <Space>
                              <Text strong style={{ fontSize: 13 }}>{item.question}</Text>
                              {item.category && <Tag size="small" color="blue">{item.category}</Tag>}
                            </Space>
                          }
                          description={<Text type="secondary" style={{ fontSize: 12 }}>{item.answer.length > 120 ? item.answer.slice(0, 120) + '…' : item.answer}</Text>}
                        />
                      </List.Item>
                    )}
                  />
                )}

                {faqJsonLd && (
                  <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text strong>FAQ JSON-LD Schema</Text>
                      <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(faqJsonLd); message.success('已複製！'); }}>複製</Button>
                    </div>
                    <Alert message="將此程式碼貼入網頁的 <head> 標籤，讓 Google AI Overview 和 ChatGPT 直接引用你的 FAQ" type="success" showIcon style={{ marginBottom: 8 }} />
                    <div style={{ background: '#1a1a2e', borderRadius: 6, padding: 12, maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: 11, color: '#a8d8ea', whiteSpace: 'pre-wrap' }}>
                      {faqJsonLd}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* FAQ Add/Edit Modal */}
      <Modal
        title={editingFaq ? '編輯 FAQ' : '新增 FAQ'}
        open={faqModal}
        onCancel={() => { setFaqModal(false); setEditingFaq(null); faqForm.resetFields(); }}
        onOk={() => faqForm.submit()}
        okText={savingFaq ? '儲存中…' : '儲存'}
        okButtonProps={{ loading: savingFaq }}
      >
        <Form form={faqForm} layout="vertical" onFinish={handleSaveFaq}>
          <Form.Item name="question" label="問題" rules={[{ required: true, message: '請填寫問題' }]}>
            <Input.TextArea rows={2} placeholder="例：你們的產品支援哪些作業系統？" />
          </Form.Item>
          <Form.Item name="answer" label="解答" rules={[{ required: true, message: '請填寫解答' }]}>
            <Input.TextArea rows={4} placeholder="詳細解答…" />
          </Form.Item>
          <Form.Item name="category" label="分類（選填）">
            <Input placeholder="例：產品功能、技術支援、價格方案" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Report Modal */}
      <Modal
        title={<Space><FileTextOutlined /> 生成 AI 品牌能見度建議方案報告</Space>}
        open={reportModal}
        onCancel={() => { setReportModal(false); reportForm.resetFields(); }}
        onOk={() => reportForm.submit()}
        okText={generatingReport ? <Space><LoadingOutlined /> 生成中…</Space> : '開始生成'}
        okButtonProps={{ loading: generatingReport }}
        cancelButtonProps={{ disabled: generatingReport }}
      >
        <Alert
          message="系統將調用 Claude AI 為每個問題關鍵字生成個人化建議，約需 1~3 分鐘，請耐心等候。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={reportForm} layout="vertical" onFinish={handleGenerateReport}>
          <Form.Item
            name="industry"
            label="品牌產業描述"
            rules={[{ required: true, message: '請輸入產業描述' }]}
            extra="例如：台灣工業泵浦製造商，水處理設備、農業灌溉"
          >
            <Input placeholder="例如：台灣保健食品品牌，視力保健、腸道健康" />
          </Form.Item>
          <Form.Item name="competitor_1" label="競品一（選填）">
            <Input placeholder="例如：Grundfos" />
          </Form.Item>
          <Form.Item name="competitor_2" label="競品二（選填）">
            <Input placeholder="例如：Goulds Pumps" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Keyword Modal */}
      <Modal title="新增關鍵字" open={keywordModal} onCancel={() => setKeywordModal(false)}
        onOk={() => keywordForm.submit()} okText="新增">
        <Form form={keywordForm} layout="vertical" onFinish={handleAddKeyword}>
          <Form.Item name="keyword_text" label="關鍵字" rules={[{ required: true, message: '請輸入關鍵字' }]}>
            <Input placeholder="例如：雲端虛擬化、HCI 超融合" />
          </Form.Item>
          <Form.Item name="keyword_category" label="類別" initialValue="brand">
            <Select options={Object.entries(CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
          </Form.Item>
          <Form.Item name="language" label="語言" initialValue="zh-TW">
            <Select options={[
              { value: 'zh-TW', label: '繁體中文' },
              { value: 'en', label: 'English' },
              { value: 'mixed', label: '中英混合' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Whitelist Modal */}
      <Modal title="新增白名單" open={whitelistModal} onCancel={() => setWhitelistModal(false)}
        onOk={() => whitelistForm.submit()} okText="新增">
        <Form form={whitelistForm} layout="vertical" onFinish={handleAddWhitelist}>
          <Form.Item name="domain_pattern" label="網域模式" rules={[{ required: true }]}>
            <Input placeholder="例如：*.comboware.com 或 comboware.com" />
          </Form.Item>
          <Form.Item name="page_type" label="頁面類型" initialValue="official">
            <Select options={[
              { value: 'official', label: '官方網站' },
              { value: 'product', label: '產品頁面' },
              { value: 'blog', label: '部落格' },
              { value: 'documentation', label: '技術文件' },
              { value: 'other', label: '其他' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Query Detail Modal */}
      <Modal
        title="查詢詳情"
        open={!!queryDetail}
        onCancel={() => setQueryDetail(null)}
        footer={null}
        width={800}
      >
        {queryDetail && (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="平台">
                <Tag color={{ claude: 'purple', perplexity: 'blue', openai: 'green', gemini: 'geekblue' }[queryDetail.ai_platform] || 'default'}>
                  {PLATFORM_LABELS[queryDetail.ai_platform] || queryDetail.ai_platform}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="狀態">
                <Tag color={queryDetail.status === 'completed' ? 'green' : 'red'}>{queryDetail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="引用數">{queryDetail.citation_count}</Descriptions.Item>
              <Descriptions.Item label="情感">
                {queryDetail.sentiment_score ? (
                  <Tag color={sentimentColor(queryDetail.sentiment_score)}>
                    {queryDetail.sentiment_score.toFixed(1)} — {queryDetail.sentiment_reason}
                  </Tag>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="模型">{queryDetail.ai_model_used || '-'}</Descriptions.Item>
              <Descriptions.Item label="Token">{queryDetail.tokens_used || '-'}</Descriptions.Item>
              <Descriptions.Item label="耗時" span={2}>{queryDetail.latency_ms ? `${(queryDetail.latency_ms / 1000).toFixed(1)}s` : '-'}</Descriptions.Item>
            </Descriptions>

            <Card title="查詢內容" size="small" style={{ marginBottom: 12 }}>
              <Paragraph>{queryDetail.query_text}</Paragraph>
            </Card>

            {queryDetail.full_response && (
              <Card title="AI 回覆" size="small" style={{ marginBottom: 12 }}>
                <div style={{ maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                  {queryDetail.full_response}
                </div>
              </Card>
            )}

            {(queryDetail.citations || []).length > 0 && (
              <Card title={`引用 URL (${queryDetail.citations.length})`} size="small">
                <List
                  size="small"
                  dataSource={queryDetail.citations}
                  renderItem={(c) => (
                    <List.Item>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Space>
                          <Badge count={c.citation_rank} style={{ backgroundColor: '#1890ff' }} />
                          <Text copyable style={{ fontSize: 13 }}>{c.url}</Text>
                          <Tag>{c.cited_page_type === 'brand_content' ? '品牌內容' : '外部內容'}</Tag>
                        </Space>
                        {c.citation_context && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            ...{c.citation_context}...
                          </Text>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
